import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { RoleGate } from '@/components/auth/RoleGate';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Link2,
  User,
  Wifi,
  FileText,
  ChevronLeft,
  ChevronRight,
  Check,
  Palette,
  QrCode,
  Building2,
  MapPin,
  Users,
  Handshake,
  Home,
  Smartphone,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { apiV1 } from '@/api/client';
import QRCodeStyling from 'qr-code-styling';
import { useRegionRequired } from '@/lib/partner';

const STEPS = [
  { id: 1, name: 'Target', description: 'Basic information' },
  { id: 2, name: 'Tracking', description: 'Attribution parameters' },
  { id: 3, name: 'Content', description: 'QR destination' },
  { id: 4, name: 'Style', description: 'Visual design' },
  { id: 5, name: 'Review', description: 'Confirm & publish' },
];

const CONTENT_TYPES = [
  { id: 'url', name: 'URL', description: 'Link to any website', icon: Link2 },
  { id: 'vcard', name: 'vCard', description: 'Contact information', icon: User },
  { id: 'wifi', name: 'WiFi', description: 'Network credentials', icon: Wifi },
  { id: 'text', name: 'Text', description: 'Plain text message', icon: FileText },
];

export default function QRWizard() {
  const regionRequired = useRegionRequired();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [designApplied, setDesignApplied] = useState(false);
  const [qrTemplates, setQrTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  
  // QR Code preview ref
  const qrPreviewRef = useRef(null);
  const qrCodeInstanceRef = useRef(null);
  
  // Reference data for dropdowns
  const [referenceData, setReferenceData] = useState({
    regions: [],
    offices: [],
    agents: [],
    partnerships: [],
  });
  
  // Filter states
  const [officeTypeFilter, setOfficeTypeFilter] = useState('all'); // 'all', 'branch', 'franchise'
  
  // Filtered options based on hierarchy
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);

  const [formData, setFormData] = useState({
    // Step 1: Target
    name: '',
    description: '',
    tags: '',
    
    // Step 2: Tracking Parameters
    regionId: '',
    regionName: '',
    officeId: '',
    officeName: '',
    agentId: '',
    agentName: '',
    partnershipId: '',
    partnershipName: '',
    listingRef: '',
    
    // Step 3: Content
    contentType: 'url',
    destinationUrl: '',
    utmCampaign: '',
    redirectType: 'dynamic', // 'dynamic' or 'direct' - only applies to URL type
    
    // WiFi fields
    wifiSsid: '',
    wifiPassword: '',
    wifiEncryption: 'WPA',
    wifiHidden: false,
    
    // vCard fields
    vcardFirstName: '',
    vcardLastName: '',
    vcardOrg: '',
    vcardTitle: '',
    vcardPhone: '',
    vcardEmail: '',
    vcardWebsite: '',
    vcardAddress: '',
    
    // Text fields
    textContent: '',
    
    // Step 4: Style
    templateId: 'default',
    design: null, // Custom design from visual editor (if any)
  });

  // Default UTM parameters for QR codes
  const DEFAULT_UTM_SOURCE = 'qr';
  const DEFAULT_UTM_MEDIUM = 'offline';

  // Check for returning from visual editor
  useEffect(() => {
    const stepParam = searchParams.get('step');
    const designAppliedParam = searchParams.get('designApplied');
    
    if (stepParam) {
      setCurrentStep(parseInt(stepParam));
    }
    
    if (designAppliedParam === 'true') {
      // First, restore the saved form state
      const savedState = sessionStorage.getItem('qr_wizard_state');
      let restoredFormData = {};
      
      if (savedState) {
        try {
          restoredFormData = JSON.parse(savedState);
          console.log('Restored form state:', restoredFormData);
        } catch (e) {
          console.error('Failed to restore form state:', e);
        }
      }
      
      // Then load the saved design from sessionStorage
      const savedDesign = sessionStorage.getItem('qr_wizard_design');
      if (savedDesign) {
        try {
          const design = JSON.parse(savedDesign);
          // Convert visual editor format to backend format
          const backendDesign = {
            dots_options: design.dotsOptions ? {
              type: design.dotsOptions.type || 'square',
              color: design.dotsOptions.color || '#000000',
              gradient: design.dotsOptions.gradient || null,
            } : null,
            corners_square_options: design.cornersSquareOptions ? {
              type: design.cornersSquareOptions.type || 'square',
              color: design.cornersSquareOptions.color || '#000000',
            } : null,
            corners_dot_options: design.cornersDotOptions ? {
              type: design.cornersDotOptions.type || 'square', 
              color: design.cornersDotOptions.color || '#000000',
            } : null,
            background_options: design.backgroundOptions ? {
              color: design.backgroundOptions.color || '#ffffff',
            } : null,
            image_options: design.logoUrl ? {
              src: design.logoUrl,
              margin: 5,
            } : null,
          };
          
          // Merge restored form state with new design
          setFormData({
            ...restoredFormData,
            design: backendDesign,
            templateId: 'custom', // Mark as custom design
          });
          setDesignApplied(true);
          toast.success('Custom design applied!');
          
          // Clean up sessionStorage
          sessionStorage.removeItem('qr_wizard_design');
          sessionStorage.removeItem('qr_wizard_state');
        } catch (e) {
          console.error('Failed to load design from editor:', e);
        }
      } else if (Object.keys(restoredFormData).length > 0) {
        // If no design but we have form state, still restore it
        setFormData(restoredFormData);
        sessionStorage.removeItem('qr_wizard_state');
      }
      
      // Clean up URL params
      navigate('/qrs/new', { replace: true });
    }
  }, [searchParams, navigate]);

  // Fetch reference data and QR templates on mount
  useEffect(() => {
    fetchReferenceData();
    fetchQRTemplates();
  }, []);

  // Update filtered offices when region or office type filter changes
  useEffect(() => {
    let filtered = referenceData.offices;
    
    // Filter by region (only show offices that have this region_id)
    if (formData.regionId) {
      filtered = filtered.filter(o => o.region_id === formData.regionId);
    }
    
    // Filter by office type
    if (officeTypeFilter !== 'all') {
      filtered = filtered.filter(o => o.office_type === officeTypeFilter);
    }
    
    setFilteredOffices(filtered);
    
    // Reset office selection if it's no longer in the filtered list
    if (formData.officeId) {
      const stillValid = filtered.some(o => o.id === formData.officeId);
      if (!stillValid) {
        updateFormData('officeId', '');
        updateFormData('officeName', '');
      }
    }
  }, [formData.regionId, officeTypeFilter, referenceData.offices]);

  // Update filtered agents when office changes
  useEffect(() => {
    let filtered = referenceData.agents;
    
    if (formData.officeId) {
      // Show only agents for the selected office
      filtered = filtered.filter(a => a.office_id === formData.officeId);
    } else if (formData.regionId) {
      // If no office selected but region is, show agents from offices in that region
      const regionOfficeIds = referenceData.offices
        .filter(o => o.region_id === formData.regionId)
        .map(o => o.id);
      filtered = filtered.filter(a => regionOfficeIds.includes(a.office_id));
    }
    
    setFilteredAgents(filtered);
    
    // Reset agent selection if it's no longer in the filtered list
    if (formData.agentId) {
      const stillValid = filtered.some(a => a.id === formData.agentId);
      if (!stillValid) {
        updateFormData('agentId', '');
        updateFormData('agentName', '');
      }
    }
  }, [formData.officeId, formData.regionId, referenceData.agents, referenceData.offices]);

  // Generate QR code preview on Step 5
  useEffect(() => {
    if (currentStep === 5 && qrPreviewRef.current && formData.destinationUrl) {
      // The published code encodes its tracked LinkHub link (/r/{code}),
      // which only exists once the code is created. The preview used to
      // encode the destination itself, so a screenshot of it skipped
      // tracking. It now shows the design with placeholder text instead.
      const previewUrl = 'LinkHub QR preview - publish to create the tracked link';

      // Get design options (either custom or default)
      const design = formData.design || {};
      
      // QR code options
      const qrOptions = {
        width: 200,
        height: 200,
        data: previewUrl,
        dotsOptions: {
          type: design.dots_options?.type || 'square',
          color: design.dots_options?.color || '#000000',
        },
        cornersSquareOptions: {
          type: design.corners_square_options?.type || 'square',
          color: design.corners_square_options?.color || '#000000',
        },
        cornersDotOptions: {
          type: design.corners_dot_options?.type || 'square',
          color: design.corners_dot_options?.color || '#000000',
        },
        backgroundOptions: {
          color: design.background_options?.color || '#ffffff',
        },
        imageOptions: {
          crossOrigin: 'anonymous',
          margin: 5,
        },
      };

      // Add logo if available - check both src and image_url for compatibility
      if (design.image_options?.image_url) {
        qrOptions.image = design.image_options.image_url;
      } else if (design.image_options?.src) {
        qrOptions.image = design.image_options.src;
      }

      // Clear previous QR code
      if (qrPreviewRef.current) {
        qrPreviewRef.current.innerHTML = '';
      }

      // Create new QR code instance
      const qrCode = new QRCodeStyling(qrOptions);
      qrCodeInstanceRef.current = qrCode;
      qrCode.append(qrPreviewRef.current);
    }
  }, [currentStep, formData.destinationUrl, formData.utmCampaign, formData.design]);

  const fetchReferenceData = async () => {
    setLoading(true);
    try {
      const response = await apiV1.get('/reference-data/options');
      if (response.data.success) {
        setReferenceData({
          regions: response.data.data.region || [],
          offices: response.data.data.office || [],
          agents: response.data.data.agent || [],
          partnerships: response.data.data.partnership || [],
        });
        setFilteredOffices(response.data.data.office || []);
        setFilteredAgents(response.data.data.agent || []);
      }
    } catch (error) {
      console.error('Failed to load reference data:', error);
      toast.error('Failed to load reference data');
    } finally {
      setLoading(false);
    }
  };

  const fetchQRTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await apiV1.get('/qrcodes/templates/');
      if (response.data.success) {
        setQrTemplates(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load QR templates:', error);
      // Don't show error toast - templates are optional
    } finally {
      setLoadingTemplates(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (field, value, options, nameField) => {
    const selected = options.find(o => o.id === value);
    updateFormData(field, value);
    updateFormData(nameField, selected?.name || '');
  };

  const progress = (currentStep / STEPS.length) * 100;

  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return formData.name.trim().length > 0;
      case 2:
        // Region is mandatory — every record must be linked to at least a region.
        return !regionRequired || !!formData.regionId;
      case 3:
        // Validate based on content type
        if (formData.contentType === 'url') {
          return formData.destinationUrl.trim().length > 0;
        } else if (formData.contentType === 'wifi') {
          return (formData.wifiSsid || '').trim().length > 0;
        } else if (formData.contentType === 'vcard') {
          return (formData.vcardFirstName || '').trim().length > 0 || 
                 (formData.vcardLastName || '').trim().length > 0;
        } else if (formData.contentType === 'text') {
          return (formData.textContent || '').trim().length > 0;
        }
        return true;
      default:
        return true;
    }
  };

  const goNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    // A region is mandatory — every record must be linked to at least a region.
    if (regionRequired && !formData.regionId) {
      toast.error('Please select a region before creating this QR code.');
      setCurrentStep(2);
      return;
    }
    setSaving(true);
    try {
      // Build destination URL based on content type
      let destinationUrl = formData.destinationUrl;
      
      if (formData.contentType === 'wifi') {
        // Build WiFi string: WIFI:T:WPA;S:network;P:password;H:hidden;;
        const encryption = formData.wifiEncryption || 'WPA';
        const ssid = formData.wifiSsid || '';
        const password = formData.wifiPassword || '';
        const hidden = formData.wifiHidden ? 'true' : 'false';
        destinationUrl = `WIFI:T:${encryption};S:${ssid};P:${password};H:${hidden};;`;
      } else if (formData.contentType === 'vcard') {
        // Build vCard string
        const vcard = [
          'BEGIN:VCARD',
          'VERSION:3.0',
          `N:${formData.vcardLastName || ''};${formData.vcardFirstName || ''};;;`,
          `FN:${formData.vcardFirstName || ''} ${formData.vcardLastName || ''}`.trim(),
          formData.vcardOrg ? `ORG:${formData.vcardOrg}` : null,
          formData.vcardTitle ? `TITLE:${formData.vcardTitle}` : null,
          formData.vcardPhone ? `TEL;TYPE=CELL:${formData.vcardPhone}` : null,
          formData.vcardEmail ? `EMAIL:${formData.vcardEmail}` : null,
          formData.vcardWebsite ? `URL:${formData.vcardWebsite}` : null,
          formData.vcardAddress ? `ADR:;;${formData.vcardAddress.replace(/\n/g, ';')};;;;` : null,
          'END:VCARD'
        ].filter(Boolean).join('\n');
        destinationUrl = vcard;
      } else if (formData.contentType === 'text') {
        destinationUrl = formData.textContent || '';
      }
      
      const payload = {
        name: formData.name,
        description: formData.description || null,
        destination_url: destinationUrl,
        qr_type: formData.contentType,
        redirect_type: formData.contentType === 'url' ? formData.redirectType : 'direct',
        tracking: {
          region_id: formData.regionId || null,
          region_name: formData.regionName || null,
          office_id: formData.officeId || null,
          office_name: formData.officeName || null,
          agent_id: formData.agentId || null,
          agent_name: formData.agentName || null,
          partnership_id: formData.partnershipId || null,
          partnership_name: formData.partnershipName || null,
          listing_ref: formData.listingRef || null,
        },
        utm: formData.contentType === 'url' ? {
          utm_source: DEFAULT_UTM_SOURCE,
          utm_medium: DEFAULT_UTM_MEDIUM,
          utm_campaign: formData.utmCampaign || null,
        } : null,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };

      // Add template or custom design
      if (formData.design && formData.templateId === 'custom') {
        // Custom design from visual editor
        payload.design = formData.design;
      } else if (formData.templateId && formData.templateId !== 'default' && formData.templateId !== 'custom') {
        // Use a template
        payload.template_id = formData.templateId;
      }

      const response = await apiV1.post('/qrcodes/', payload);
      
      if (response.data.success) {
        toast.success('QR Code created successfully!', {
          description: `Short code: ${response.data.data.short_code}`,
        });
        navigate('/qrs');
      }
    } catch (error) {
      console.error('Failed to create QR code:', error);
      toast.error('Failed to create QR code', {
        description: error.response?.data?.detail || 'Please try again',
      });
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">QR Code Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Summer Campaign - Property 123"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
                data-testid="qr-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Add a description to help identify this QR code"
                value={formData.description}
                onChange={(e) => updateFormData('description', e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma separated)</Label>
              <Input
                id="tags"
                placeholder="e.g., marketing, print, flyer"
                value={formData.tags}
                onChange={(e) => updateFormData('tags', e.target.value)}
              />
            </div>
          </div>
        );

      case 2:
        if (loading) {
          return (
            <div className="space-y-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          );
        }
        
        if (!regionRequired) {
          return (
            <p className="text-sm text-muted-foreground" data-testid="tracking-national-note">
              This will be national Pam Golding Properties content. Partner accounts don't link records to a region or office.
            </p>
          );
        }

        return (
          <div className="space-y-6">
            <div className="text-sm text-muted-foreground mb-4">
              <p>Link this QR code to the part of the business it belongs to. A region is required; office, agent and partnership are optional.</p>
            </div>

            {/* Region */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                Region <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.regionId || "none"}
                onValueChange={(value) => handleSelectChange('regionId', value === "none" ? '' : value, referenceData.regions, 'regionName')}
              >
                <SelectTrigger data-testid="tracking-region-select" aria-required="true">
                  <SelectValue placeholder="Select a region (required)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {referenceData.regions.map(region => (
                    <SelectItem key={region.id} value={region.id}>
                      {region.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!formData.regionId && (
                <p className="text-xs text-muted-foreground">
                  Required — you can only pick regions you have access to.
                </p>
              )}
            </div>

            {/* Office Type Filter */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                Office Type
              </Label>
              <Select
                value={officeTypeFilter}
                onValueChange={(value) => setOfficeTypeFilter(value)}
              >
                <SelectTrigger data-testid="tracking-office-type-select">
                  <SelectValue placeholder="Filter by office type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="branch">Branch</SelectItem>
                  <SelectItem value="franchise">Franchise</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Filter the office list by type
              </p>
            </div>

            {/* Office */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                Office
                {filteredOffices.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    ({filteredOffices.length} available)
                  </span>
                )}
              </Label>
              <Select
                value={formData.officeId || "none"}
                onValueChange={(value) => handleSelectChange('officeId', value === "none" ? '' : value, filteredOffices, 'officeName')}
              >
                <SelectTrigger data-testid="tracking-office-select">
                  <SelectValue placeholder="Select office (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {filteredOffices.map(office => (
                    <SelectItem key={office.id} value={office.id}>
                      {office.name}
                      {office.office_type && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({office.office_type})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.regionId && filteredOffices.length === 0 && (
                <p className="text-xs text-amber-600">
                  No offices found for the selected region{officeTypeFilter !== 'all' ? ` and type "${officeTypeFilter}"` : ''}
                </p>
              )}
            </div>

            {/* Agent */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Agent
                {filteredAgents.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    ({filteredAgents.length} available)
                  </span>
                )}
              </Label>
              <Select
                value={formData.agentId || "none"}
                onValueChange={(value) => handleSelectChange('agentId', value === "none" ? '' : value, filteredAgents, 'agentName')}
              >
                <SelectTrigger data-testid="tracking-agent-select">
                  <SelectValue placeholder="Select agent (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {filteredAgents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.officeId && filteredAgents.length === 0 && (
                <p className="text-xs text-amber-600" data-testid="agent-empty-hint">
                  No agents found in the selected office. Agent is optional —
                  leave it blank, or try a different office.
                </p>
              )}
            </div>

            {/* Partnership */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Handshake className="h-4 w-4 text-primary" />
                Partnership
                {referenceData.partnerships.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    ({referenceData.partnerships.length} available)
                  </span>
                )}
              </Label>
              <Select
                value={formData.partnershipId || "none"}
                onValueChange={(value) => handleSelectChange('partnershipId', value === "none" ? '' : value, referenceData.partnerships, 'partnershipName')}
              >
                <SelectTrigger data-testid="tracking-partnership-select">
                  <SelectValue placeholder="Select partnership (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {referenceData.partnerships.map(partnership => (
                    <SelectItem key={partnership.id} value={partnership.id}>
                      {partnership.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground" data-testid="partnership-scope-hint">
                Partnerships are a global list — they aren't filtered by region or
                office. Leave blank if none apply.
              </p>
            </div>

            {/* Listing Reference */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Home className="h-4 w-4 text-primary" />
                Listing Reference
                <span className="text-xs text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <Input
                placeholder="Leave blank if not for a property listing"
                value={formData.listingRef}
                onChange={(e) => updateFormData('listingRef', e.target.value)}
                data-testid="tracking-listing-ref-input"
              />
              <p className="text-xs text-muted-foreground">
                Only fill in for QR codes tied to a specific property — e.g.
                <code className="mx-1 px-1 bg-muted rounded">PROP-2024-001</code>.
                Skip for development pages, branding QRs, or anything not a
                listing.
              </p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <Label>Content Type</Label>
              <RadioGroup
                value={formData.contentType}
                onValueChange={(value) => updateFormData('contentType', value)}
                className="grid grid-cols-2 gap-4"
              >
                {CONTENT_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <Label
                      key={type.id}
                      htmlFor={type.id}
                      className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                        formData.contentType === type.id
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted'
                      }`}
                    >
                      <RadioGroupItem value={type.id} id={type.id} className="sr-only" />
                      <Icon className="h-5 w-5 text-primary" />
                      <div>
                        <div className="font-medium">{type.name}</div>
                        <div className="text-xs text-muted-foreground">{type.description}</div>
                      </div>
                      {formData.contentType === type.id && (
                        <Check className="h-4 w-4 text-primary ml-auto" />
                      )}
                    </Label>
                  );
                })}
              </RadioGroup>
            </div>

            {formData.contentType === 'url' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="url">Destination URL *</Label>
                  <Input
                    id="url"
                    type="url"
                    placeholder="https://example.com/your-page"
                    value={formData.destinationUrl}
                    onChange={(e) => updateFormData('destinationUrl', e.target.value)}
                    data-testid="qr-url-input"
                  />
                </div>

                <Separator />

                {/* Every LinkHub QR code is tracked and editable */}
                <div className="p-3 bg-muted/50 rounded-lg" data-testid="qr-tracked-note">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Zap className="h-4 w-4 text-primary" />
                    Tracked and editable
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    The QR code points at a LinkHub link that redirects to this URL. Every scan is counted, and you can change the destination later without reprinting.
                  </p>
                </div>

                <Separator />

                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium">UTM Tracking</h4>
                    <p className="text-sm text-muted-foreground">
                      Auto-included: <code className="text-xs bg-muted px-1 rounded">utm_source=qr</code> <code className="text-xs bg-muted px-1 rounded">utm_medium=offline</code>
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="utm_campaign">Campaign Name (optional)</Label>
                    <Input
                      id="utm_campaign"
                      placeholder="e.g., summer-2024, product-launch"
                      value={formData.utmCampaign}
                      onChange={(e) => updateFormData('utmCampaign', e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '-'))}
                      data-testid="qr-campaign-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Added as utm_campaign parameter
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* WiFi Content Type */}
            {formData.contentType === 'wifi' && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <Wifi className="h-4 w-4 inline mr-2" />
                    Scanning this QR code will prompt users to connect to your WiFi network automatically.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="wifi-ssid">Network Name (SSID) *</Label>
                  <Input
                    id="wifi-ssid"
                    placeholder="Your WiFi Network Name"
                    value={formData.wifiSsid || ''}
                    onChange={(e) => updateFormData('wifiSsid', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="wifi-password">Password</Label>
                  <Input
                    id="wifi-password"
                    type="password"
                    placeholder="Network password (leave blank if open)"
                    value={formData.wifiPassword || ''}
                    onChange={(e) => updateFormData('wifiPassword', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="wifi-encryption">Security Type</Label>
                  <Select
                    value={formData.wifiEncryption || 'WPA'}
                    onValueChange={(value) => updateFormData('wifiEncryption', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select security type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="WPA">WPA/WPA2/WPA3</SelectItem>
                      <SelectItem value="WEP">WEP</SelectItem>
                      <SelectItem value="nopass">Open (No Password)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="wifi-hidden"
                    checked={formData.wifiHidden || false}
                    onCheckedChange={(checked) => updateFormData('wifiHidden', checked)}
                  />
                  <Label htmlFor="wifi-hidden">Hidden Network</Label>
                </div>
              </div>
            )}

            {/* vCard Content Type */}
            {formData.contentType === 'vcard' && (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    <User className="h-4 w-4 inline mr-2" />
                    Scanning this QR code will add your contact information to the user's phone contacts.
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vcard-firstname">First Name *</Label>
                    <Input
                      id="vcard-firstname"
                      placeholder="John"
                      value={formData.vcardFirstName || ''}
                      onChange={(e) => updateFormData('vcardFirstName', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vcard-lastname">Last Name *</Label>
                    <Input
                      id="vcard-lastname"
                      placeholder="Doe"
                      value={formData.vcardLastName || ''}
                      onChange={(e) => updateFormData('vcardLastName', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="vcard-org">Organization</Label>
                  <Input
                    id="vcard-org"
                    placeholder="Company Name"
                    value={formData.vcardOrg || ''}
                    onChange={(e) => updateFormData('vcardOrg', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="vcard-title">Job Title</Label>
                  <Input
                    id="vcard-title"
                    placeholder="Sales Director"
                    value={formData.vcardTitle || ''}
                    onChange={(e) => updateFormData('vcardTitle', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="vcard-phone">Phone Number</Label>
                  <Input
                    id="vcard-phone"
                    type="tel"
                    placeholder="+27 12 345 6789"
                    value={formData.vcardPhone || ''}
                    onChange={(e) => updateFormData('vcardPhone', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="vcard-email">Email</Label>
                  <Input
                    id="vcard-email"
                    type="email"
                    placeholder="john.doe@company.com"
                    value={formData.vcardEmail || ''}
                    onChange={(e) => updateFormData('vcardEmail', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="vcard-website">Website</Label>
                  <Input
                    id="vcard-website"
                    type="url"
                    placeholder="https://www.company.com"
                    value={formData.vcardWebsite || ''}
                    onChange={(e) => updateFormData('vcardWebsite', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="vcard-address">Address</Label>
                  <Textarea
                    id="vcard-address"
                    placeholder="123 Main Street&#10;City, State 12345&#10;Country"
                    value={formData.vcardAddress || ''}
                    onChange={(e) => updateFormData('vcardAddress', e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Text Content Type */}
            {formData.contentType === 'text' && (
              <div className="space-y-4">
                <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
                  <p className="text-sm text-purple-800 dark:text-purple-200">
                    <FileText className="h-4 w-4 inline mr-2" />
                    This QR code will display plain text when scanned. Perfect for messages, codes, or instructions.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="text-content">Text Content *</Label>
                  <Textarea
                    id="text-content"
                    placeholder="Enter your text message here..."
                    value={formData.textContent || ''}
                    onChange={(e) => updateFormData('textContent', e.target.value)}
                    rows={6}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    {(formData.textContent || '').length} / 2000 characters
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {/* System QR Templates */}
            {loadingTemplates ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="p-4">
                    <Skeleton className="h-20 w-20 mx-auto rounded-lg mb-2" />
                    <Skeleton className="h-4 w-20 mx-auto" />
                  </Card>
                ))}
              </div>
            ) : qrTemplates.length > 0 ? (
              <>
                <div>
                  <h4 className="font-medium mb-3">Saved Templates</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {qrTemplates.map((template) => (
                      <Card
                        key={template.id}
                        className={`cursor-pointer transition-all ${
                          formData.templateId === template.id 
                            ? 'border-primary ring-2 ring-primary/20' 
                            : 'hover:bg-muted hover:border-muted-foreground/30'
                        }`}
                        onClick={() => {
                          updateFormData('templateId', template.id);
                          // Also apply the design from the template
                          updateFormData('design', template.design);
                        }}
                      >
                        <CardContent className="p-4 text-center">
                          {template.preview_url ? (
                            <img 
                              src={template.preview_url} 
                              alt={template.name}
                              className="h-20 w-20 mx-auto rounded-lg object-contain mb-2"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`h-20 w-20 mx-auto rounded-lg flex items-center justify-center mb-2 ${template.preview_url ? 'hidden' : ''}`}
                            style={{ 
                              backgroundColor: template.design?.background_options?.color || '#f3f4f6',
                            }}
                          >
                            <QrCode 
                              className="h-10 w-10" 
                              style={{ color: template.design?.dots_options?.color || '#374151' }}
                            />
                          </div>
                          <p className="text-sm font-medium truncate">{template.name}</p>
                          {template.category && template.category !== 'custom' && (
                            <p className="text-xs text-muted-foreground capitalize">{template.category}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
                <Separator />
              </>
            ) : null}

            {/* Default + Custom Design are admin-only. Viewers and editors may
                only pick a brand-approved saved template above, so off-brand QR
                codes can't be produced by non-admins. RoleGate hides these
                silently (no flash for a viewer). templateId defaults to
                'default', so a non-admin who picks nothing still gets a valid
                black-and-white QR. */}
            <RoleGate role="admin">
              {/* Default style option */}
              <div>
                <h4 className="font-medium mb-3">{qrTemplates.length > 0 ? 'Or use default style' : 'Style Options'}</h4>
                <Card
                  className={`cursor-pointer transition-all max-w-[200px] ${
                    formData.templateId === 'default'
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'hover:bg-muted hover:border-muted-foreground/30'
                  }`}
                  onClick={() => {
                    updateFormData('templateId', 'default');
                    updateFormData('design', null);
                  }}
                >
                  <CardContent className="p-4 text-center">
                    <div className="h-20 w-20 mx-auto bg-white border rounded-lg flex items-center justify-center mb-2">
                      <QrCode className="h-10 w-10 text-gray-900" />
                    </div>
                    <p className="text-sm font-medium">Default</p>
                    <p className="text-xs text-muted-foreground">Standard black & white</p>
                  </CardContent>
                </Card>
              </div>

              <Separator />

              {/* Custom Design Option */}
              <Card className="p-4 border-dashed border-2 hover:border-primary/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <Palette className="h-4 w-4" />
                      Custom Design
                    </p>
                    <p className="text-sm text-muted-foreground">Open the visual editor for full customization</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Save current form state to sessionStorage before navigating
                      sessionStorage.setItem('qr_wizard_state', JSON.stringify(formData));
                      sessionStorage.setItem('qr_wizard_design', JSON.stringify({
                        dotsOptions: formData.design?.dots_options,
                        backgroundOptions: formData.design?.background_options,
                        cornersSquareOptions: formData.design?.corners_square_options,
                        cornersDotOptions: formData.design?.corners_dot_options,
                        logoUrl: formData.design?.logo_url,
                      }));
                      navigate('/editor?from=wizard&step=4');
                    }}
                    data-testid="open-visual-editor-button"
                  >
                    Open Editor
                  </Button>
                </div>
              </Card>

              {/* Show currently applied design indicator */}
              {designApplied && formData.templateId === 'custom' && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                    <Check className="h-4 w-4" />
                    Custom design applied from visual editor
                  </p>
                </div>
              )}
            </RoleGate>
          </div>
        );

      case 5:
        // Build the final URL with tracking parameters
        const buildFinalUrl = () => {
          if (!formData.destinationUrl) return '';
          let url = formData.destinationUrl;
          const params = [];
          
          params.push(`utm_source=${DEFAULT_UTM_SOURCE}`);
          params.push(`utm_medium=${DEFAULT_UTM_MEDIUM}`);
          
          if (formData.utmCampaign) {
            params.push(`utm_campaign=${formData.utmCampaign}`);
          }
          
          if (params.length > 0) {
            const separator = url.includes('?') ? '&' : '?';
            url = url + separator + params.join('&');
          }
          return url;
        };
        
        const finalUrl = buildFinalUrl();
        
        // Collect tracking info
        const trackingInfo = [];
        if (formData.regionName) trackingInfo.push({ label: 'Region', value: formData.regionName });
        if (formData.officeName) trackingInfo.push({ label: 'Office', value: formData.officeName });
        if (formData.agentName) trackingInfo.push({ label: 'Agent', value: formData.agentName });
        if (formData.partnershipName) trackingInfo.push({ label: 'Partnership', value: formData.partnershipName });
        if (formData.listingRef) trackingInfo.push({ label: 'Listing Ref', value: formData.listingRef });
        
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Review Your QR Code</CardTitle>
                <CardDescription>Please review the details before publishing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{formData.name || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">QR Type</p>
                    <p className="font-medium capitalize">
                      {formData.contentType === 'url'
                        ? 'Tracked URL'
                        : formData.contentType
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Content Type</p>
                    <p className="font-medium capitalize">{formData.contentType}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Style</p>
                    <p className="font-medium capitalize">
                      {formData.templateId === 'custom' 
                        ? 'Custom Design' 
                        : formData.templateId === 'default'
                        ? 'Default'
                        : qrTemplates.find(t => t.id === formData.templateId)?.name || formData.templateId || 'Default'
                      }
                    </p>
                  </div>
                </div>
                
                {trackingInfo.length > 0 && (
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium mb-3">Tracking Parameters</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {trackingInfo.map(item => (
                        <div key={item.label} className="text-sm">
                          <p className="text-muted-foreground">{item.label}</p>
                          <p className="font-medium">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {finalUrl && (
                  <div className="pt-4 border-t space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Base Destination</p>
                      <p className="text-sm font-medium truncate">{formData.destinationUrl}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Final Tracked URL</p>
                      <div className="p-3 bg-muted rounded-lg">
                        <code className="text-xs break-all text-primary">{finalUrl}</code>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Live QR Code Preview */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Design Preview
                </CardTitle>
                <CardDescription>
                  Preview of your QR code design. Scans will be tracked after publishing.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center p-6 bg-white rounded-lg border">
                  <div 
                    ref={qrPreviewRef} 
                    className="flex items-center justify-center"
                    data-testid="qr-preview-container"
                  >
                    {!formData.destinationUrl && (
                      <div className="h-[200px] w-[200px] flex items-center justify-center text-muted-foreground">
                        <QrCode className="h-24 w-24" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground text-center">
                    <strong>Note:</strong> This preview shows your destination URL directly. 
                    After publishing, the QR code will use a tracking URL (e.g., <code className="bg-muted px-1 rounded">go.domain.com/r/ABC123</code>) 
                    that records each scan before redirecting to your destination.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="qr-wizard-page">
      <PageHeader
        title="Create QR Code"
        description="Follow the steps to create a new QR code with tracking"
      />

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">
            Step {currentStep}: {STEPS[currentStep - 1].name}
          </span>
          <span className="text-muted-foreground">
            {currentStep} of {STEPS.length}
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Step indicators */}
      <div className="hidden sm:flex justify-between">
        {STEPS.map((step, index) => (
          <button
            key={step.id}
            onClick={() => step.id <= currentStep && setCurrentStep(step.id)}
            disabled={step.id > currentStep}
            className={`flex items-center gap-2 text-sm ${
              step.id === currentStep
                ? 'text-primary font-medium'
                : step.id < currentStep
                ? 'text-muted-foreground cursor-pointer hover:text-foreground'
                : 'text-muted-foreground/50 cursor-not-allowed'
            }`}
            data-testid="qr-wizard-step-tab"
          >
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                step.id === currentStep
                  ? 'bg-primary text-primary-foreground'
                  : step.id < currentStep
                  ? 'bg-primary/20 text-primary'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {step.id < currentStep ? <Check className="h-3 w-3" /> : step.id}
            </span>
            <span className="hidden md:inline">{step.name}</span>
          </button>
        ))}
      </div>

      {/* Step content */}
      <Card>
        <CardHeader>
          <CardTitle>{STEPS[currentStep - 1].name}</CardTitle>
          <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
        </CardHeader>
        <CardContent>{renderStepContent()}</CardContent>
      </Card>

      {/* Navigation footer */}
      <div className="flex items-center justify-between border-t pt-4">
        <Button
          variant="outline"
          onClick={goBack}
          disabled={currentStep === 1}
          data-testid="qr-wizard-back-button"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        {currentStep < STEPS.length ? (
          <Button onClick={goNext} disabled={!canGoNext()} data-testid="qr-wizard-next-button">
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={saving} data-testid="qr-wizard-submit-button">
            {saving ? 'Publishing...' : 'Publish QR Code'}
          </Button>
        )}
      </div>
    </div>
  );
}
